import axios from 'axios';
import XMLParser from 'react-xml-parser';
import { treeItemFetch } from './TreeItemURL';
import {
	CHILD_DEFAULT_NODES,
	CMIC_TREE_NODE_TYPES,
	CMIC_STATIC_TREE_NODE_ID,
	CMIC_FIELD_REST__NAMES,
	PAGE_LIMIT,
	DOC_PAGE_LIMIT,
	ACTION_ROLE,
	CMIC_KEYS,
} from '../shared/Constants';
import { handleFetchedData } from './handleFetchedData';
import { handleLoginUserViaAuth2, isInternetAvailable } from '../shared/UtilityFunctions';
import { TripleDES } from 'crypto-js';
import { Buffer } from 'buffer';
import { triggerSegmentTrackCall, triggerSegmentPageCall } from './segment/segmentUtility';

export const fetchData = (data, nodeType, objServerSettings) => {
	try {
		return new Promise(async (resolve, reject) => {
			if (nodeType === CMIC_STATIC_TREE_NODE_ID.CMIC_ECM) {
				resolve({
					mainData: CHILD_DEFAULT_NODES,
					infoData: null,
				});
			}

			const { _strApiURL, argsMap, statusData = {} } = await treeItemFetch(data, nodeType, objServerSettings);

			if (_strApiURL == '' || !_strApiURL) {
				resolve({
					mainData: {
						items: [],
						hasMore: false,
						totalResults: 0,
					},
					infoData: [],
				});
				return;
			}

			axios
				.get(_strApiURL, {
					...handleLoginUserViaAuth2(objServerSettings),
				})
				.then(async resp => {
					triggerSegmentTrackCall(data, objServerSettings, 'success', nodeType, resp?.data?.items[0]);
					triggerSegmentPageCall(data, objServerSettings, 'success', nodeType, resp?.data?.items[0]);
					// For REST API
					if (resp.data.items || nodeType === CMIC_TREE_NODE_TYPES.PROJECT) {
						const endResult = await handleFetchedData(nodeType, resp.data, objServerSettings);

						resolve({
							mainData: { statusData, ...endResult },
							infoData: [],
						});
						return;
					}
					// For XML Based Web Service
					const response = new XMLParser().parseFromString(resp.data, 'application/xml');
					let result = response?.children;

					if (
						!result ||
						result.length <= 0 ||
						result[0].children[0].value === 'NO ATTACHMENT AVAILABLE FOR THIS ITEM.'
					)
						return resolve({
							mainData: {
								items: [],
								hasMore: false,
								totalResults: 0,
							},
							infoData: [],
							noData: true,
						});

					const endResult = await handleFetchedData(nodeType, result);

					resolve({
						mainData: {
							items: endResult,
							hasMore: false,
							totalResults: 0,
						},
					});
				})
				.catch(err => {
					triggerSegmentTrackCall(data, objServerSettings, 'fail', nodeType);
					triggerSegmentPageCall(data, objServerSettings, 'fail', nodeType);
					console.log('fetchData.js error=', err);
					resolve({
						mainData: {
							items: [],
							hasMore: false,
							totalResults: 0,
						},
						infoData: [],
					});
				});
		});
	} catch (error) {
		console.log('fetchData.js fetchData() outer Error', error);
		resolve({
			mainData: {
				items: [],
				hasMore: false,
				totalResults: 0,
			},
			infoData: [],
		});
	}
};

export const fetchFieldsData = async (
	webserviceName,
	objServerSettings,
	params = { offset: 0, projOraseq: '', firstName: '' },
) => {
	return new Promise((resolve, reject) => {
		let _strApiURL = '';

		if (webserviceName === CMIC_FIELD_REST__NAMES.contacts) {
			_strApiURL = `${objServerSettings.serverURL}${
				CMIC_FIELD_REST__NAMES.contacts
			}?onlyData=true&totalResults=false&limit=${PAGE_LIMIT}&offset=${
				PAGE_LIMIT * params.offset
			}&orderBy=PmpcFirstName,PmpcLastName&onlyData=true&finder=searchbyname;projOraseq=${
				params.projOraseq
			},program=RFI,searchCriteria=${params.firstName}`;
		}

		if (webserviceName === CMIC_FIELD_REST__NAMES.rfistatus) {
			_strApiURL = `${objServerSettings.serverURL}${CMIC_FIELD_REST__NAMES.rfistatus}?offset=0&limit=100&totalResults=false&orderBy=PmrfisStatusName&onlyData=true&finder=selectbystatusclasscode;statusClassCode=P`;
		}

		if (_strApiURL !== '') {
			axios
				.get(_strApiURL, {
					...handleLoginUserViaAuth2(objServerSettings),
				})
				.then(resp => {
					if (webserviceName === CMIC_FIELD_REST__NAMES.contacts) {
						resolve(resp.data);
					} else {
						resolve(resp.data.items);
					}
				})
				.catch(err => {
					console.log(err);
					return resolve([]);
				});
		}
	});
};

export const handleUserlogout = async (objServerSettings, setUserInfo, navigate, setIsLoading, setShowToast) => {
	if (!isInternetAvailable(setShowToast)) {
		return;
	}
	let _strAPIURL = `${objServerSettings.serverURL}${CMIC_FIELD_REST__NAMES.LOGOUT}`;
	setIsLoading(true);

	try {
		const result = await axios.get(_strAPIURL, {
			...handleLoginUserViaAuth2(objServerSettings),
		});

		handleSaveInfoWithoutPWD(objServerSettings, setUserInfo);
		setIsLoading(false);
		navigate.replace('/settings');
		return result?.data;
	} catch (error) {
		console.log(error);
		handleSaveInfoWithoutPWD(objServerSettings, setUserInfo);
		setIsLoading(false);
		navigate.replace('/settings');
	}
};

const handleSaveInfoWithoutPWD = (userData, setUserInfo) => {
	const requiredInfo = { ...userData, password: '', accessToken: '' };

	let ciphertext = TripleDES.encrypt(
		JSON.stringify(requiredInfo),
		CMIC_KEYS.SERVER_SETTINGS_ENCRYPTION_KEY,
	).toString();

	const encodedString = Buffer.from(ciphertext).toString('base64');

	// setting userInfo after submitting data
	setUserInfo({
		...requiredInfo,
	});

	localStorage.setItem(CMIC_KEYS.SERVER_SETTINGS_ENCRYPTION_KEY, encodedString); // SAVING ENCRYPTED KEY
};

export const getApiInfoService = async objServerSettings => {
	let _strAPIURL = '';

	if (objServerSettings.actionRole === ACTION_ROLE.ECM) {
		_strAPIURL = `${objServerSettings.serverURL}${CMIC_FIELD_REST__NAMES.ecmRestApiInfo}`;
	} else {
		_strAPIURL = `${objServerSettings.serverURL}${CMIC_FIELD_REST__NAMES.fieldRestApiInfo}`;
	}

	try {
		const result = await axios.get(_strAPIURL, {
			...handleLoginUserViaAuth2(objServerSettings),
		});
		return result?.data;
	} catch (error) {
		console.log(error);
	}
};

export const getPMDocument = async (objServerSettings, VUuid, ecmParentNodeType, params) => {
	let _strAPIURL = '';
	let { limit = DOC_PAGE_LIMIT, orderBy = '', offset = 0 } = params;
	switch (ecmParentNodeType) {
		case CMIC_TREE_NODE_TYPES.PROJECT_RFI:
			_strAPIURL = `${objServerSettings.serverURL}${
				CMIC_FIELD_REST__NAMES.rfisv12
			}/${VUuid}/child/RelatedDocumentView?limit=${limit}${
				orderBy ? '&orderBy=' + orderBy : '&orderBy=SysrdDate:desc'
			}&offset=${offset}&totalResults=true&onlyData=true&expand=DocRevisionView,DocumentsView`;
			break;

		case CMIC_TREE_NODE_TYPES.PROJECT_ISSUE:
			_strAPIURL = `${objServerSettings.serverURL}${
				CMIC_FIELD_REST__NAMES.issues
			}/${VUuid}/child/RelatedDocumentView?limit=${limit}${
				orderBy ? '&orderBy=' + orderBy : '&orderBy=SysrdDate:desc'
			}&offset=${offset}&totalResults=true&onlyData=true&expand=DocRevisionView,DocumentsView`;
			break;

		case CMIC_TREE_NODE_TYPES.PROJECT_SUBMITTALS:
			_strAPIURL = `${objServerSettings.serverURL}${
				CMIC_FIELD_REST__NAMES.submittals
			}/${VUuid}/child/RelatedDocumentView?limit=${limit}${
				orderBy ? '&orderBy=' + orderBy : '&orderBy=SysrdDate:desc'
			}&offset=${offset}&totalResults=true&onlyData=true&expand=DocRevisionView,DocumentsView`;
			break;

		case CMIC_TREE_NODE_TYPES.PROJECT_DAILY_JOURNAL:
			_strAPIURL = `${objServerSettings.serverURL}${
				CMIC_FIELD_REST__NAMES.journals
			}/${VUuid}/child/RelatedDocumentView?limit=${limit}${
				orderBy ? '&orderBy=' + orderBy : '&orderBy=SysrdDate:desc'
			}&offset=${offset}&totalResults=true&onlyData=true&expand=DocRevisionView,DocumentsView`;
			break;
		default:
			_strAPIURL = '';
	}

	try {
		const result = await axios.get(_strAPIURL, {
			...handleLoginUserViaAuth2(objServerSettings),
		});
		return result;
	} catch (error) {
		console.log(error);
	}
};

export const fetchUserRole = async (projId, docType, userInfo) => {
	const URL_MADE = `${userInfo.serverURL}${CMIC_FIELD_REST__NAMES.userRoles}?project-uuid=${projId}&program-code=${docType}&onyData=true`;
	try {
		const result = await axios.get(URL_MADE, {
			...handleLoginUserViaAuth2(userInfo),
		});
		return result.data;
	} catch (error) {
		console.log('fetchUserRole() Error', error);
	}
};
